import { useCallback, useEffect, useMemo } from 'react'

import { ProductViewModel } from '@/types/api'

import { VariantContextValuesType } from './types'

import { generateVendorDetails } from '../_utils/generate-vendor-details'
import { getProductPricing } from '../_utils/get-product-pricing'
import { getVariantSummary } from '../_utils/get-variant-summary'

interface Params {
    product?: ProductViewModel
    isLoading: boolean
    selectedAtpId: string | null
    variantUpdateHandler: (atpId: string | null) => void
}

export const useProductVariantCore = ({ product, isLoading, selectedAtpId, variantUpdateHandler }: Params) => {
    const variants = useMemo(() => product?.variants || [], [product])
    const {
        uniqueColorVariants,
        uniqueWeightVariants,
        hasAtLeastOnePharmacy,
        isFullyShipBySeller,
        isFullyWithLeadTime,
        isFullyWithoutShipBySeller,
        minimumLeadTime,
        hasAtleastOneWeightUnit,
    } = useMemo(() => getVariantSummary(variants || []), [variants])

    const hasColorOptions = uniqueColorVariants.length > 0
    const hasWeightOptions = uniqueWeightVariants.length > 1
    const hasNoVariantOptions = !hasColorOptions && !hasWeightOptions

    const selectedVariant = useMemo(() => {
        if (!selectedAtpId) return null

        return variants?.find((item) => item.id === selectedAtpId) || null
    }, [selectedAtpId, variants])

    const defaultVariant = useMemo(() => {
        if (!hasNoVariantOptions) return null

        return variants?.at(0) || null
    }, [hasNoVariantOptions, variants])

    const defaultWeight = useMemo(() => {
        if (uniqueWeightVariants.length === 1) {
            return uniqueWeightVariants.at(0)?.weight || null
        }
        return null
    }, [uniqueWeightVariants])

    const activeVariant = selectedVariant || defaultVariant

    const activePricing = useMemo(() => {
        return getProductPricing(product, activeVariant || undefined)
    }, [product, activeVariant])

    const vendorDetails = useMemo(() => {
        return generateVendorDetails({
            activeVariant,
            hasAtLeastOnePharmacy,
            isFullyShipBySeller,
            isFullyWithLeadTime,
            isFullyWithoutShipBySeller,
            minimumLeadTime,
            hasNoVariantOptions,
        })
    }, [
        hasNoVariantOptions,
        activeVariant,
        hasAtLeastOnePharmacy,
        isFullyShipBySeller,
        isFullyWithLeadTime,
        isFullyWithoutShipBySeller,
        minimumLeadTime,
    ])

    const noVendorDetails = !vendorDetails.leadTimeText && !vendorDetails.vendorText && !vendorDetails.tooltipText

    const selectVariant = useCallback(
        (colorId: string | null, weight: number | null) => {
            if (hasNoVariantOptions) return

            const variant = variants?.find((item) => {
                if (colorId && weight) {
                    return item.color?.id === colorId && item.weight?.value === weight
                }

                if (colorId && !weight) {
                    return item.color?.id === colorId
                }

                if (!colorId && weight) {
                    return item.weight?.value === weight
                }

                return false
            })

            const atpId = variant?.id

            if (atpId) {
                variantUpdateHandler(atpId)
            } else {
                variantUpdateHandler(null)
            }
        },
        [variantUpdateHandler, variants, hasNoVariantOptions]
    )

    useEffect(() => {
        if (!hasNoVariantOptions || isLoading) return

        variantUpdateHandler(null)
    }, [hasNoVariantOptions, variantUpdateHandler, isLoading])

    const contextValue: VariantContextValuesType = {
        allVariants: variants,
        selectedVariant,
        defaultVariant,
        activeVariant,
        activePricing,
        defaultWeight,
        hasColorOptions,
        hasWeightOptions,
        hasAtLeastOnePharmacy,
        hasAtleastOneWeightUnit,
        isFullyShipBySeller,
        isFullyWithLeadTime,
        isFullyWithoutShipBySeller,
        selectVariant,
        uniqueColorVariants,
        uniqueWeightVariants,
        vendorDetails,
        noVendorDetails,
    }

    return contextValue
}
